import type { APIError } from 'pkg/api/models';
import * as sdk from 'pkg/core/sdk';

import { Event, trackEvent } from 'components/analytics';

/**
 *	Sends an create request for a single resource.
 *
 *	@param string endpoint
 *	@param PayloadType payload
 *
 *	@returns Promise<[Response, ResponseType]>
 */
export async function create<PayloadType, ResponseType>(
	endpoint: string,
	payload: PayloadType,
	additionalHeaders: { [key: string]: any } = {}
): Promise<[Response, ResponseType, APIError?]> {
	const request = await sdk.post(endpoint, {}, payload, additionalHeaders);

	if (!request.ok) {
		const response: APIError = await request.json();
		return [request, null, response];
	}

	const response: ResponseType = await request.json();

	return [request, response, null];
}

/**
 *	Reactivates a single resource by id
 *
 *	@param string endpoint
 *
 *	@returns Promise<[Response, ResponseType]>
 */
export async function reactivate(
	endpoint: string
): Promise<[Response, ResponseType, APIError?]> {
	const request = await sdk.post(endpoint, {}, {});

	if (!request.ok) {
		const error: APIError = await request.json();
		return [request, null, error];
	}

	const response: ResponseType = await request.json();

	return [request, response];
}

/**
 *	Sends an create request for CSV exports.
 *
 *	@param string endpoint
 *	@param PayloadType payload
 *
 *	@returns Promise<[boolean, string]>
 */
export async function createExport<UriPattern, PayloadType>(
	endpoint: string,
	payload?: PayloadType,
	uriPattern?: UriPattern,
	getRequest?: boolean
): Promise<[boolean, string]> {
	const request = await sdk[getRequest ? 'get' : 'post'](
		endpoint,
		uriPattern || {},
		payload
	);

	if (request.ok) {
		trackEvent(Event.Export, { endpoint });
		const text = await request.text();
		return [true, text];
	}

	return [false, ''];
}
